import React, { useState, useEffect } from 'react';
import Card from 'react-bootstrap/Card';
import axios from 'axios';
import { useAuth } from "./AuthProvider";
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { ADMIN, GAME_MASTER, PLAYER } from './Roles';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { GameStatus } from './GameStatus';
import Decisions from './game/Decisions';
import GameSetup from './game/GameSetup';
import HoverHelp from './game/HoverHelp';

const startGame = ({ id, starting, setStarting, auth, setGame, setError }) => {
    setStarting(true);
    axios.put(`/api/games/${id}`, {}, {
      headers: {
        Authorization: `Bearer ${auth.token}`,
      },
      params: {
          status: "ROUND_ACTIVE"
      },
    })
      .then(response => {
        setGame(response.data);
        setStarting(false);
      })
      .catch(error => {
        console.log(error);
        setError(error);
        setStarting(false);
      });
};

const finishRound = (id, round, setFinishingRound, auth, setGame, setError) => {
    setFinishingRound(true);
    axios.put(`/api/games/${id}`, {}, {
      headers: {
        Authorization: `Bearer ${auth.token}`,
      },
      params: {
          status: "ROUND_FINISHED"
      },
    })
      .then(response => {
        setGame(response.data);
        setFinishingRound(false);
      })
      .catch(error => {
        console.log(error);
        setError(error);
        setFinishingRound(false);
      });
};

const startNextRound = (id, round, setFinishingRound, auth, setGame, setError) => {
    setFinishingRound(true);
    axios.put(`/api/games/${id}`, {}, {
      headers: {
        Authorization: `Bearer ${auth.token}`,
      },
      params: {
          status: "ROUND_ACTIVE"
      },
    })
      .then(response => {
        setGame(response.data);
        setFinishingRound(false);
      })
      .catch(error => {
        console.log(error);
        setError(error);
        setFinishingRound(false);
      });
};

const allPlayersHaveSubmitted = (decisions, round, teams) => {
    if (decisions && round && teams) {
        const teamNames = teams.map(t => t.name);
        const teamsWithDecisions = [];
        teamNames.forEach(team => {
            if (decisions.decisions[team] && decisions.decisions[team][round]) {
                teamsWithDecisions.push(team);
            }
        });
        return teamNames.length === teamsWithDecisions.length;
    } else {
        return false;
    }
}

const DeleteButton = ({ id, onDelete }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { t } = useTranslation();
    return (
        <>
          <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
              {t('games.delete')}
          </Button>
          <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>{t('game.confirm-deletion-title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {t('game.confirm-deletion')}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" onClick={() => onDelete(id) && showDeleteModal(false)}>
                {t('common.confirmation')}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
    );
};

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

const teamHasValidMember = (team) => {
    return (team.members ?? []).some(member => isNonEmptyString(member.name) && isNonEmptyString(member.email));
};

const gameCanStart = (game) => {
    const teams = game?.teams ?? [];
    const playableTeams = teams.filter(team => !team.automaton);
    const teamsWithPasswords = playableTeams.filter(team => isNonEmptyString(team.password));
    return playableTeams.length === teamsWithPasswords.length && playableTeams.every(teamHasValidMember);
};

const isDecisionEntered = (decision) => {
    return decision?.value !== undefined && decision.value !== null && `${decision.value}`.trim().length > 0;
};

const currentRoundDecisionProgress = (game) => {
    const totalDecisions = game?.decisionDefinitions?.length ?? 0;
    const round = game?.round;
    return (game?.teams ?? []).map((team, index) => {
        const decisions = game?.decisionValues?.decisions?.[team.name]?.[round] ?? {};
        const enteredDecisions = Object.values(decisions).filter(isDecisionEntered);
        const updatedAtValues = enteredDecisions
            .map(decision => decision.updatedAt)
            .filter(Boolean)
            .map(updatedAt => new Date(updatedAt).getTime())
            .filter(updatedAt => !Number.isNaN(updatedAt));
        const lastUpdatedAt = updatedAtValues.length > 0 ? new Date(Math.max(...updatedAtValues)) : undefined;
        const percentage = totalDecisions > 0 ? Math.round((enteredDecisions.length / totalDecisions) * 100) : 0;
        return {
            rowNumber: index + 1,
            teamName: team.name,
            memberCount: team.members?.length ?? 0,
            percentage,
            lastUpdatedAt,
        };
    });
};

const showDecisionProgress = (game) => {
    return game?.status === "ROUND_ACTIVE" || game?.status === "ROUND_FINISHED";
};

const GameDetails = ({ id }) => {
    const auth = useAuth();
    const navigate = useNavigate();
    const [game, setGame] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [starting, setStarting] = useState(false);
    const [finishingRound, setFinishingRound] = useState(false);
    const { t, i18n } = useTranslation();

    const onDelete = (id) => {
        setLoading(true);
        axios.delete(`/api/games/${id}`, {
            headers: {
                Authorization: `Bearer ${auth.token}`,
            },
        })
        .then(response => {
            setLoading(false);
            navigate(`/games`);
        })
        .catch(error => {
            console.log(error);
            setError(error);
            setLoading(false);
        });
    };

    const reloadGame = (id) => {
        setLoading(true);
        axios.get(`/api/games/${id}`, {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        })
        .then(response => {
          setGame(response.data);
          setLoading(false);
        })
        .catch(error => {
          console.log(error);
          setError(error);
          setLoading(false);
        });
    };

    useEffect(() => {
         setLoading(true);
         axios.get(`/api/games/${id}`, {
           headers: {
             Authorization: `Bearer ${auth.token}`,
           },
         })
           .then(response => {
             setGame(response.data);
             setLoading(false);
           })
           .catch(error => {
             console.log(error);
             setError(error);
             setLoading(false);
           });
       }, [auth.token, id]);

       if (loading) return <p>{t('common.loading')}</p>;
       if (error) return <p>{t('game.loading-error')}: {error.message}</p>;

       return (
        <>
            <h1>{game.name}</h1>
            <Card border="0">
              <Card.Body>
                <GameStatus status={game?.status} t={t} />
                <Card.Text>
                  {(auth.roles.includes(ADMIN) || auth.roles.includes(GAME_MASTER)) &&
                      <>
                        {t('games.password')}: {game?.password}
                        <HoverHelp id="game-password" text={t('passwords.help.game-password')} />
                      </>
                  }
                </Card.Text>
                <Card.Text>
                  {(auth.roles.includes(ADMIN) || auth.roles.includes(GAME_MASTER)) &&
                      <>
                        {t('games.master-password')}: {game?.masterPassword}
                        <HoverHelp id="game-master-password" text={t('passwords.help.game-master-password')} />
                      </>
                  }

                </Card.Text>
                <Card.Text>
                  {game?.status === "ROUND_ACTIVE" && <>{t('games.round')} {game.round} / {game.rounds}</>}
                  {game?.status === "INITIALIZED" && <>{t('games.rounds')} {game.rounds}</>}
                </Card.Text>
                <Card.Text>
                  {t('games.round-deadline')}: {game.deadline ? new Date(game.deadline[game.round - 1]).toLocaleDateString(i18n.language) : ''}
                </Card.Text>
                <Card.Text>
                  {t('games.started')}: {game.startedAt ? new Date(game.startedAt).toLocaleDateString(i18n.language) : ''}
                </Card.Text>
                {auth.roles.includes(PLAYER) &&
                    <Col md={8} className="mb-3">
                      {t('games.market-overview')}: {game.marketOverview[game.round]?.descriptions?.[i18n.language]}
                    </Col>
                }
                {(auth.roles.includes(ADMIN) || auth.roles.includes(GAME_MASTER)) &&
                    <Card.Body>
                        <Col md={8}>
                          <Table striped bordered>
                            <thead>
                                <tr>
                                    <th key="round">{t('games.round')}</th>
                                    <th key="market-demand">{t('games.market-demand')} ({t('unit.boxes')})</th>
                                    <th key="market-overview">{t('games.market-overview')}</th>
                                    <th key="round-deadline">{t('games.round-deadline')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    game.marketDemand.map((demand, round) => {
                                        const marketOverview = game.marketOverview[round]?.descriptions?.[i18n.language];
                                        return <tr key={round}><td>{round + 1}</td><td>{demand}</td><td>{marketOverview}</td><td>{new Date(game.deadline[round]).toLocaleDateString(i18n.language)}</td></tr>
                                    })
                                }
                            </tbody>
                          </Table>
                        </Col>
                    </Card.Body>
                }
                {(auth.roles.includes(ADMIN) || auth.roles.includes(GAME_MASTER)) &&
                    <div className="mt-4">
                        {showDecisionProgress(game) &&
                            <Col md={8}>
                                <Table striped bordered className="mb-3">
                                    <thead>
                                        <tr>
                                            <th>{t('game.decision-progress.row-number')}</th>
                                            <th>{t('game.decision-progress.team-name')}</th>
                                            <th>{t('game.decision-progress.member-count')}</th>
                                            <th>{t('game.decision-progress.decisions-entered')}</th>
                                            <th>{t('game.decision-progress.last-updated-at')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentRoundDecisionProgress(game).map(row =>
                                            <tr key={row.teamName}>
                                                <td>{row.rowNumber}</td>
                                                <td>{row.teamName}</td>
                                                <td>{row.memberCount}</td>
                                                <td>{row.percentage}%</td>
                                                <td>{row.lastUpdatedAt ? row.lastUpdatedAt.toLocaleString(i18n.language) : ''}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </Col>
                        }
                        {game.status === "INITIALIZED" && gameCanStart(game) &&
                        <Button variant="primary" onClick={() => startGame({ id, starting, setStarting, auth, setGame, setError })}>
                          {t('game.start')}
                        </Button>}
                        {game.status === "ROUND_ACTIVE" &&
                        <Button variant="primary" onClick={() => finishRound(id, game?.round, setFinishingRound, auth, setGame, setError)} disabled={!allPlayersHaveSubmitted(game?.decisionValues, game?.round, game?.teams) || finishingRound}>
                          {game?.rounds === game?.round ? t('game.finish') : t('game.finish-round')}
                        </Button>}
                        {game.status === "ROUND_FINISHED" &&
                        <Button variant="primary" onClick={() => startNextRound(id, game?.round, setFinishingRound, auth, setGame, setError)} disabled={!allPlayersHaveSubmitted(game?.decisionValues, game?.round, game?.teams) || finishingRound}>
                          {t('game.start-round')}
                        </Button>}
                    </div>
                }
                {auth.roles.includes(ADMIN) && <div className="mt-4">
                    <DeleteButton id={game.id} onDelete={onDelete} />
                </div>}
              </Card.Body>
            </Card>
            {game.status === 'SETUP' &&
                <GameSetup game={game} reloadGame={reloadGame} />
            }
            {game.status !== "SETUP" &&
                <Decisions game={game} reloadGame={reloadGame} />
            }
        </>
       );
};

export default GameDetails;
